function findByText(text, exact = false) {
	const els = document.querySelectorAll('span, p, label, h6, h5, h4, button, .MuiTypography-root')
	for (const el of els) {
		const content = el.textContent.trim().toLowerCase()
		if (exact ? content === text.toLowerCase() : content.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

const ES = navigator.language?.startsWith('es')

// The Batch List card (the real table, or the tour's sample one when there are no batches) and its data rows
function batchTable() {
	const papers = document.querySelectorAll('.MuiPaper-root, .MuiCard-root')
	for (const paper of papers) {
		if (!paper.textContent.includes('Batch List')) continue
		const rows = [...paper.querySelectorAll('table tbody tr')].filter(r => r.querySelectorAll('td').length >= 5 && !r.textContent.includes('no matching records'))
		if (rows.length) return { paper, rows }
	}
	return null
}

// The Status cell of a stopped batch (the richest one), else of the first batch
function statusCell() {
	const table = batchTable()
	if (!table) return null
	const row = table.rows.find(r => r.textContent.includes('Stopped')) || table.rows[0]
	const cells = row.querySelectorAll('td')
	return cells[cells.length - 1]
}

// Several elements as one box (the engine only reads its rect and scrolls to it)
function groupBox(els) {
	return {
		getBoundingClientRect() {
			const rs = els.map(e => e.getBoundingClientRect())
			const top = Math.min(...rs.map(r => r.top)), left = Math.min(...rs.map(r => r.left))
			const bottom = Math.max(...rs.map(r => r.bottom)), right = Math.max(...rs.map(r => r.right))
			return { top, left, bottom, right, width: right - left, height: bottom - top }
		},
		scrollIntoView: opts => els[0].scrollIntoView(opts),
	}
}

export const TOUR_STEPS = [
	{
		type: 'intro',
		icon: 'clipboard-list',
		title: ES ? 'Bienvenido a Lotes' : 'Welcome to Batches',
		description: ES
			? 'La lista de lotes muestra todos los procesos de búsqueda ejecutados. Cada lote agrupa múltiples búsquedas en distintos sitios web y oficinas. Monitoriza el progreso en tiempo real, descarga resultados en Excel, consulta datos históricos o detén lotes en ejecución.'
			: 'The Batch List shows all executed search processes. Each batch groups multiple searches across sites and offices. Monitor progress in real time, download results as Excel, view historical data, or stop running batches.',
	},
	{
		type: 'field',
		// The «Date Range» field of the filter panel above the table
		findTarget: () => document.querySelector('.rs-picker-daterange')?.closest('.MuiFormControl-root'),
		icon: 'calendar-days',
		title: ES ? 'Filtro de rango de fechas' : 'Date Range Filter',
		description: ES
			? 'Filtra los lotes por fecha de creación. Por defecto muestra los lotes de hoy. Selecciona un rango más amplio para ver procesos anteriores. Solo se permiten fechas pasadas y la actual.'
			: 'Filter batches by creation date. By default it shows today\'s batches. Select a wider date range to see older processes. Only past and current dates are allowed.',
	},
	{
		type: 'field',
		findTarget: () => batchTable()?.paper,
		skipIf: () => !findByText('Batch List'),
		icon: 'table-cells',
		title: ES ? 'Tabla de lotes' : 'Batch List Table',
		description: ES
			? 'La tabla muestra todos los lotes con: ID del lote (pasa el ratón para verlo entero), Sitios (portales web consultados), Oficinas (puntos de recogida), número de búsquedas, fecha de creación y estado actual. Ordena pulsando la cabecera de una columna o filtra con el icono de filtro para encontrar lotes específicos.'
			: 'The table displays all batches with: Batch ID (hover to read it whole), Sites (web portals searched), Offices (pickup locations), number of searches, creation date, and current status. Click a column header to sort, or use the filter icon to find specific batches.',
	},
	{
		type: 'field',
		findTarget: statusCell,
		skipIf: () => !findByText('Batch List'),
		icon: 'signal',
		title: ES ? 'Columna de estado' : 'Status Column',
		description: ES
			? 'La columna más detallada. Para lotes en ejecución: una barra de progreso clicable con el porcentaje y las tareas completadas/totales (haz clic para ver el detalle por sitio). Para lotes detenidos: la hora de parada y una etiqueta roja «Stopped». Para todos los lotes: contador de errores (ámbar, icono de advertencia) y contador N/A (rojo, icono de calendario) que muestran problemas en las búsquedas.'
			: 'The most detailed column. For running batches: a clickable progress bar with the percentage and completed/total tasks (click it to view per-site details). For stopped batches: the stop time and a red "Stopped" chip. For all batches: error count (amber, warning icon) and N/A count (red, calendar icon) showing search issues.',
	},
	{
		type: 'field',
		// The action icons at the end of the Status cell
		findTarget: () => {
			const buttons = [...(statusCell()?.querySelectorAll('button') || [])]
			return buttons.length ? groupBox(buttons) : null
		},
		skipIf: () => !findByText('Batch List'),
		icon: 'download',
		title: ES ? 'Botones de acción' : 'Action Buttons',
		description: ES
			? 'Cada fila de lote tiene botones de acción. «Download Export» (nube, si está disponible): descarga el archivo exportado. «View Historical» (reloj): abre las búsquedas del lote en la página de Historial. «Download Batch Excel» (hoja de cálculo): descarga todos los resultados del lote en un archivo Excel.'
			: 'Each batch row has action buttons. Download Export (cloud, if available): download the exported file. View Historical (clock): opens the batch searches in the History page. Download Batch Excel (spreadsheet): downloads all batch results as an Excel file.',
		example: '☁️ Download Export | 🕐 View Historical | 📊 Download Batch Excel',
	},
	{
		type: 'field',
		findTarget: () => {
			const checkboxes = document.querySelectorAll('table thead input[type="checkbox"], table thead .MuiCheckbox-root')
			if (checkboxes.length > 0) return checkboxes[0].closest('th') || checkboxes[0]
			return null
		},
		skipIf: () => !document.querySelector('table thead input[type="checkbox"]') && !document.querySelector('table thead .MuiCheckbox-root'),
		icon: 'circle-stop',
		title: ES ? 'Selección múltiple y detención' : 'Multi-Select & Stop',
		description: ES
			? 'Selecciona varios lotes usando las casillas de verificación de cada fila. Al seleccionar filas, aparece una barra flotante con un botón «Stop» para forzar la detención de todos los lotes en ejecución seleccionados a la vez.'
			: 'Select multiple batches using the checkboxes on each row. When rows are selected, a floating bar appears with a "Stop" button to force-stop all selected running batches at once.',
	},
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo listo!' : "You're All Set!",
		description: ES
			? 'Usa el rango de fechas para filtrar lotes, monitoriza los procesos en ejecución mediante la barra de progreso y descarga los resultados cuando estén listos. Haz clic en la barra de progreso de un lote en ejecución para ver el detalle por sitio. Usa la selección múltiple para detener lotes en bloque.'
			: 'Use the date range to filter batches, monitor running processes via the progress bar, and download results when ready. Click the progress bar of a running batch to see per-site details. Use multi-select to stop batches in bulk.',
	},
]
